import { useState } from 'react';
import { Controller } from 'react-hook-form';
import copy from 'copy-to-clipboard';
import {
  InputText,
  DateInput,
  Select,
  SelectSize,
  InputSize,
  formatDate,
  SelectType,
  Autocomplete,
  PickerPosition,
  IconCopy,
  IconSelected,
} from '@ui/src';
import { BaseInfoFormValues } from '../../../hoc/individual/types';
import { GeneralUserInfoRowTypes } from '../types';

import cn from './overview-content.module.css';

const GeneralUserInfoRow = ({
  control,
  type,
  list,
  name,
  disabled,
  title,
  children,
  defaultValue,
  selectType = SelectType.SINGLE,
  zIndex,
  pickerPisition = PickerPosition.LEFT,
  error,
  withCopy,
  birthdayPicker,
}: GeneralUserInfoRowTypes): React.JSX.Element => {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const onCopy = () => {
    if (defaultValue) {
      copy(defaultValue);

      setCopyIsSuccess(true);

      setTimeout(() => {
        setCopyIsSuccess(false);
      }, 2000);
    }
  };

  let content;

  if (!disabled) {
    if (type === 'text') {
      content = (
        <Controller
          control={control}
          name={name as keyof BaseInfoFormValues}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name={name as string}
              placeholder=""
              className={cn.textInput}
              onChange={onChange}
              value={value as string}
              size={InputSize.SMALL}
              disabled={disabled}
              error={error}
            />
          )}
        />
      );
    }

    if (type === 'date') {
      content = (
        <Controller
          control={control}
          name={name as keyof BaseInfoFormValues}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <DateInput
              value={value as string}
              onChange={(date) => onChange(new Date(date).toISOString())}
              className={cn.dateOfBirthInput}
              pickerPisition={pickerPisition}
              error={error}
              birthdayPicker={birthdayPicker}
              withoutCustomSelector
            />
          )}
        />
      );
    }

    if (type === 'select' && list?.length) {
      content = (
        <Controller
          control={control}
          name={name as keyof BaseInfoFormValues}
          defaultValue={defaultValue}
          render={({ field: { onChange, value } }) => (
            <Select
              list={list}
              placeholder="Choose value"
              name={name}
              selectWrapperClassName={cn.overviewSelectWrapper}
              value={(value as string) || defaultValue}
              onChange={onChange}
              size={SelectSize.SMALL}
              mode={selectType}
              error={error}
            />
          )}
        />
      );
    }

    if (type === 'autocomplete' && list?.length) {
      content = (
        <Controller
          control={control}
          name={name as keyof BaseInfoFormValues}
          defaultValue={defaultValue}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              list={list}
              placeholder="Choose value"
              name={name}
              value={(value as string) || defaultValue}
              className={`${cn.infoRowAutocomplete} ${cn.overviewAutocompleteWrapper}`}
              onChange={onChange}
              size={SelectSize.SMALL}
              mode={selectType}
              error={error}
            />
          )}
        />
      );
    }
  } else {
    content = defaultValue;

    if (type === 'date') {
      content =
        typeof defaultValue === 'string' ? formatDate(defaultValue) : '-';
    }
  }

  return (
    <div className={cn.generalUserInfoRow} style={{ zIndex }}>
      <div className={cn.generalUserInfoRowTitle}>{title}</div>
      <div className={cn.generalUserInfoRowValue}>
        {content}
        {children}
        {withCopy && (
          <button
            type="button"
            className={cn.copyButton}
            aria-label={title}
            onClick={onCopy}
          >
            {copyIsSuccess ? (
              <IconSelected
                width={12}
                height={12}
                className={cn.copySuccessIcon}
              />
            ) : (
              <IconCopy width={14} height={14} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default GeneralUserInfoRow;
